import { DEFAULT_SEARCH_MATCH_MODE, normalizeAviPillar } from "@/constants";
import type { Role } from "@/lib/permissions";
import { isDeadlineCompletedForRole } from "@/lib/status";
import type {
  AviDeadlineOutput,
  AviItem,
  AviListParams,
  PaginatedAviListOutput,
  RecentAviItem,
  SearchMatchMode,
  WorkflowOutput,
} from "@/types";

export const workflowAviMock: WorkflowOutput = {
  total: 100,
  items: [
    { status: "created", count: 4, percentage: 4 },
    { status: "drafting", count: 10, percentage: 10 },
    { status: "le_review", count: 26, percentage: 26 },
    { status: "pm_review", count: 10, percentage: 10 },
    { status: "approved", count: 50, percentage: 50 },
  ],
};

export const deadlineAviMock: AviDeadlineOutput = {
  on_track: 5,
  near_deadline: 3,
  overdue: 4,
  completed: 5,
  total: 17,
  on_track_pct: 29.41,
  near_deadline_pct: 17.65,
  overdue_pct: 23.53,
  completed_pct: 29.41,
};

export const recentAviMock: RecentAviItem[] = [
  {
    paa_id: 101,
    paa_uuid: "ade55556-777f-43fb-8ffc-914b75388980",
    avi_id: "02f47c32-c53d-49d8-a7a1-2a22cb747cb4",
    avi_code: "3.1.1",
    pillar: "S",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    site_name: "Fábrica",
    assets: ["Forquilha II"],
    last_accessed_at: "2026-03-30T19:16:24.197Z",
  },
  {
    paa_id: 102,
    paa_uuid: "794e8697-2466-4695-87fa-122cbd19f8dc",
    avi_id: "02f47c32-c53d-49d8-a7a1-2a22cb74xyz0",
    avi_code: "3.1.2",
    pillar: "S",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III"],
    last_accessed_at: "2026-03-25T11:07:03.078Z",
  },
  {
    paa_id: 103,
    paa_uuid: "6ed9762d-17d6-4102-9bc5-6edb2b91898c",
    avi_id: "02f47c32-c53d-49d8-a7a1-2a22cb74xyz1",
    avi_code: "88.88.88.88",
    pillar: "S",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV"],
    last_accessed_at: "2026-03-20T19:16:24.197Z",
  },
  {
    paa_id: 104,
    paa_uuid: "bb88804b-7494-4421-a3e7-8764b75ef47b",
    avi_id: "02f47c32-c53d-49d8-a7a1-2a22cb74xyz2",
    avi_code: "3.1.4",
    pillar: "S",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    last_accessed_at: "2026-03-18T05:42:02.321Z",
  },
  {
    paa_id: 105,
    paa_uuid: "9f737f3b-04e2-4fe2-807e-e5c8d21b5102",
    avi_id: "02f47c32-c53d-49d8-a7a1-2a22cb74xyz3",
    avi_code: "88.88.88.88",
    pillar: "S",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    site_name: "Fábrica",
    assets: ["Forquilha I", "Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    last_accessed_at: "2026-03-12T14:16:24.197Z",
  },
];

export const aviItemsMock: AviItem[] = [
  {
    paa_id: 100,
    paa_uuid: "fd407be0-5ce9-4aa4-a136-7db5e2c71fb0",
    avi_id: "1",
    avi_code: "3.1.1",
    pillar: "GISTM",
    status: "pm_review",
    deadline_status: "near_deadline",
    description:
      "Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle?",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    client_name: "Vale",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    score: 0,
    due_date: "2024-12-31",
    is_favorite: true,
  },
  {
    paa_id: 101,
    paa_uuid: "af56a430-7a9d-479f-9fd0-eb072dc4cdf2",
    avi_id: "1",
    avi_code: "3.1.1",
    pillar: "GISTM",
    status: "created",
    deadline_status: "on_track",
    description:
      "Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle?",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    client_name: "Vale",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    score: 2,
    due_date: "2024-12-31",
    is_favorite: false,
  },
  {
    paa_id: 102,
    paa_uuid: "d77863fd-8435-4ce2-a6ab-8bcbdaf734b3",
    avi_id: "1",
    avi_code: "3.1.1",
    pillar: "GISTM",
    status: "drafting",
    deadline_status: "on_track",
    description:
      "Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle?",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    client_name: "Vale",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    score: 1,
    due_date: "2024-12-31",
    is_favorite: true,
  },
  {
    paa_id: 103,
    paa_uuid: "a756a3fd-0aeb-4829-86bd-945f4e72d57e",
    avi_id: "1",
    avi_code: "3.1.1",
    pillar: "GISTM",
    status: "le_review",
    deadline_status: "on_track",
    description:
      "Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle?",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    client_name: "Vale",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    score: 0,
    due_date: "2024-12-31",
    is_favorite: false,
  },
  {
    paa_id: 104,
    paa_uuid: "279ca6bf-b474-46b1-b11d-d6f7a54f711e",
    avi_id: "1",
    avi_code: "3.1.1",
    pillar: "GISTM",
    status: "approved",
    deadline_status: "on_track",
    description:
      "Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle?",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    client_name: "Vale",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    score: 3,
    due_date: "2024-12-31",
    is_favorite: true,
  },
  {
    paa_id: 105,
    paa_uuid: "093e9786-3263-4bfc-a2f6-88fd1e213551",
    avi_id: "1",
    avi_code: "3.1.1",
    pillar: "GISTM",
    status: "pm_review",
    deadline_status: "overdue",
    description:
      "Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle? Does the operator demonstrate the use of knowledge about climate change (prolonged droughts, intense rainfall, temperature rise) in opportunity assessment at each stage of the TSF life cycle?",
    project_code: "GIST-VAL-FAB-001",
    standard_acronym: "GISTM",
    client_name: "Vale",
    site_name: "Fábrica",
    assets: ["Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    score: 3,
    due_date: "2024-12-31",
    is_favorite: false,
  },
];

export const paginatedAviListMock = {
  total: aviItemsMock.length,
  page: 1,
  page_size: 10,
  items: aviItemsMock,
};

function matchesSearch(
  item: AviItem,
  search: string,
  matchMode: SearchMatchMode = DEFAULT_SEARCH_MATCH_MODE
) {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return [
    item.avi_code,
    item.description ?? "",
    item.project_code,
    item.standard_acronym,
    item.site_name,
    item.client_name,
    ...item.assets,
  ].some((value) =>
    matchMode === "contains"
      ? value.toLowerCase().includes(normalizedSearch)
      : value.toLowerCase().startsWith(normalizedSearch)
  );
}

const AVI_CODE_COLLATOR = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

function sortItems(
  items: AviItem[],
  sort: AviListParams["sort"],
  sortBy?: AviListParams["sort_by"]
) {
  const sorted = [...items].sort((left, right) => {
    if (sortBy === "code") {
      const codeComparison = AVI_CODE_COLLATOR.compare(left.avi_code, right.avi_code);

      if (codeComparison !== 0) {
        return sort === "desc" ? -codeComparison : codeComparison;
      }

      return left.project_code.localeCompare(right.project_code);
    }

    const leftDate = left.due_date ?? "";
    const rightDate = right.due_date ?? "";

    if (leftDate !== rightDate) {
      return leftDate.localeCompare(rightDate);
    }

    return left.paa_id - right.paa_id;
  });

  return sortBy === "code" || sort !== "desc" ? sorted : sorted.reverse();
}

function matchesDeadlineFilter(item: AviItem, params: AviListParams) {
  if (!params.deadline_status?.length) return true;

  const isCompleted = isDeadlineCompletedForRole(params.role as Role, item.status);

  return params.deadline_status.some((status) =>
    status === "completed" ? isCompleted : !isCompleted && item.deadline_status === status
  );
}

export function getMockAviList(
  params: AviListParams,
  options?: { favoritesOnly?: boolean }
): PaginatedAviListOutput {
  const favoritesOnly = options?.favoritesOnly ?? false;
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? 10;

  let filteredItems = aviItemsMock.filter((item) => {
    if (favoritesOnly && !item.is_favorite) return false;
    if (params.search && !matchesSearch(item, params.search, params.match_mode)) return false;
    if (params.project_code && item.project_code !== params.project_code) return false;
    if (params.site_name && item.site_name !== params.site_name) return false;
    if (params.standard_acronym && item.standard_acronym !== params.standard_acronym) return false;
    if (params.pillar?.length) {
      const itemPillar = normalizeAviPillar(item.pillar);
      if (!itemPillar || !params.pillar.includes(itemPillar)) return false;
    }
    if (params.workflow_status?.length && !params.workflow_status.includes(item.status))
      return false;
    if (!matchesDeadlineFilter(item, params)) return false;
    if (
      params.asset_names?.length &&
      !params.asset_names.some((asset) => item.assets.includes(asset))
    ) {
      return false;
    }

    return true;
  });

  filteredItems = sortItems(filteredItems, params.sort, params.sort_by);

  const total = filteredItems.length;
  const start = (page - 1) * pageSize;
  const items = filteredItems.slice(start, start + pageSize);

  return {
    total,
    page,
    page_size: pageSize,
    items,
  };
}
